import { useEffect, useRef } from 'react';
import { type MediaHandlerProps, useMediaUtils } from '..';

enum Event {
  LOAD_PROGRESS = 'loadProgress',
  PLAY_PROGRESS = 'playProgress',
  PLAY = 'play',
  PAUSE = 'pause',
  FINISH = 'finish',
  SEEK = 'seek',
  READY = 'ready',
  OPEN_SHARE_PANEL = 'sharePanelOpened',
  CLICK_DOWNLOAD = 'downloadClicked',
  CLICK_BUY = 'buyClicked',
  ERROR = 'error',
}

type SoundCloudEventData = {
  widgetId?: string;
  method?: string;
  value?: unknown;
};

type PlayProgressEvent = {
  currentPosition: number;
  loadedProgress: number;
  relativePosition: number;
  soundId: string;
};

// https://developers.soundcloud.com/docs/api/html5-widget#api
type Widget = {
  // methods
  bind: (event: Event, listener: (data: unknown) => void) => void;
  unbind: (event: Event) => void;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  seekTo: (position: number) => void;
  setVolume: (volume: number) => void;
  next: () => void;
  prev: () => void;
  skip: (index: number) => void;

  // getters
  getVolume: (callback: (data: unknown) => void) => void;
  getDuration: (callback: (data: unknown) => void) => void;
  getPosition: (callback: (data: unknown) => void) => void;
  getSounds: (callback: (data: unknown) => void) => void;
  getCurrentSound: (callback: (data: unknown) => void) => void;
  getCurrentSoundIndex: (callback: (data: unknown) => void) => void;
  isPaused: (callback: (data: unknown) => void) => void;
};

export const useSoundCloudHandler = ({ eventName }: MediaHandlerProps) => {
  const soundId = useRef<string | null>(null);
  const {
    isFirstPlay,
    push,
    getIframe,
    millisecondsToSeconds,
    getPercentage,
    playbackDuration,
    isScriptLoaded,
    loadScript,
  } = useMediaUtils(eventName);

  const defaultData = () => ({
    sound_id: soundId.current,
  });

  const initWidget = () => {
    if (!window.SC) return;

    const iframe = getIframe();
    if (!iframe) return;

    // create the widget
    const widget = window.SC.Widget(iframe) as Widget;

    widget.bind(Event.READY, () => {
      // get the duration
      widget.getDuration((_duration) => {
        playbackDuration.current = millisecondsToSeconds(_duration as number);
      });
    });

    // on the play event
    widget.bind(Event.PLAY, (data) => {
      if (isFirstPlay.current) return;

      // get the sound id
      if (!soundId.current) soundId.current = (data as { soundId: string })?.soundId;

      // get the duration
      if (playbackDuration.current <= 0) {
        widget.getDuration((_duration) => {
          playbackDuration.current = millisecondsToSeconds(_duration as number);
        });
      }

      // send the first play event
      isFirstPlay.current = true;
      push('play', defaultData());
    });

    widget.bind(Event.PLAY_PROGRESS, (data: PlayProgressEvent | unknown) => {
      if (!data) {
        return;
      }
      // check that we have duration
      widget.getDuration((_duration) => {
        if (playbackDuration.current <= 0) {
          playbackDuration.current = millisecondsToSeconds(_duration as number);
        }
      });
      if (playbackDuration.current <= 0) return;

      const duration = playbackDuration.current * 1000; // duration in milliseconds
      const position = (data as PlayProgressEvent)?.currentPosition ?? 0;

      const percentage = getPercentage(position, duration);
      if (!!percentage) push(percentage, defaultData());
    });
  };

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (!event.origin.includes('.soundcloud.com')) return;

      try {
        const data = JSON.parse(event.data) as SoundCloudEventData;

        if (data?.method === 'ready') {
          if (isScriptLoaded.current === true) {
            initWidget();
          } else {
            loadScript('https://w.soundcloud.com/player/api.js', () => {
              initWidget();
            });
          }
        }
      } catch (error) {}
    };

    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
    };
  }, []);
};
